import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  FiberNew as FiberNewIcon,
  InfoOutlined as InfoOutlinedIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Upcoming as UpcomingIcon,
} from '@mui/icons-material';
import {
  PricePromoUnit,
  PricePromoWatchlist,
  PricePromoWatchlistInput,
  PricePromoWatchlistRegion,
} from '../../services/api';
import PricePromoPriceButton from './PricePromoPriceButton';
import BinocularsIcon from './BinocularsIcon';
import AnticipatePricePromoTab, { AnticipatePricePromoTabProps } from './AnticipatePricePromoTab';
import {
  buildDemoWatchlist,
  DEMO_WATCHLIST_EXPLANATION,
  DEMO_WATCHLIST_ID,
  getWatchlistMatches,
  isPenthouseUnit,
  PENTHOUSE_FILTER_VALUE,
  WATCHLIST_ALL_SECTION_EXPLANATION,
  WATCHLIST_LATEST_DAYS,
  WATCHLIST_MATCH_EXPLANATION,
} from './pricePromoWatchlistMatching';

/** Tabs of the Price Promo Watchlist dialog. */
export type PricePromoWatchlistDialogTab = 'watchlist' | 'anticipate';

const REGION_OPTIONS: PricePromoWatchlistRegion[] = ['CCR', 'RCR', 'OCR'];
const BEDROOM_OPTIONS = [1, 2, 3, 4, 5, PENTHOUSE_FILTER_VALUE];
const DISTRICT_OPTIONS = Array.from({ length: 28 }, (_, i) => `D${i + 1}`);

const formatBudget = (value: number): string => `$${Math.round(value).toLocaleString()}`;

const bedroomLabel = (b: number): string => (b === PENTHOUSE_FILTER_VALUE ? 'PH' : `${b}BR`);

/** "1BR · 3BR · PH" — numeric counts ascending, PH last. */
const formatBedrooms = (bedrooms: number[]): string =>
  [...bedrooms]
    .sort((a, b) => (a === PENTHOUSE_FILTER_VALUE ? 1 : b === PENTHOUSE_FILTER_VALUE ? -1 : a - b))
    .map(bedroomLabel)
    .join(' · ');

/** Compact district summary: "D5 · D9" or "5 districts" when long. */
const formatDistricts = (districts: string[]): string =>
  districts.length > 3
    ? `${districts.length} districts`
    : [...districts].sort((a, b) => Number(a.slice(1)) - Number(b.slice(1))).join(' · ');

/** Toggles `value` in a multi-select array. */
const toggleValue = <T,>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter((v) => v !== value) : [...values, value];

/** Formats the budget field as the user types: digits only, thousands separators (e.g. "180,000"). */
const formatBudgetInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  return digits ? Number(digits).toLocaleString('en-US') : '';
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

interface WatchlistFormState {
  clientName: string;
  mobile: string;
  bedrooms: number[];
  budget: string;
  regions: PricePromoWatchlistRegion[];
  districts: string[];
}

const EMPTY_FORM: WatchlistFormState = {
  clientName: '',
  mobile: '',
  bedrooms: [],
  budget: '',
  regions: [],
  districts: [],
};

interface PricePromoWatchlistDialogProps {
  open: boolean;
  onClose: () => void;
  /** Active tab; controlled by the parent so nudges can deep-link to Anticipate. */
  tab: PricePromoWatchlistDialogTab;
  onTabChange: (tab: PricePromoWatchlistDialogTab) => void;
  /** Props forwarded verbatim to the Anticipate Price Promo tab. */
  anticipate: AnticipatePricePromoTabProps;
  /** Unseen anticipate promo count — badge on the Anticipate tab. */
  anticipateNewCount: number;
  /** Market-wide price promo units feed (same data as the Price Promo Units dialog). */
  units: PricePromoUnit[];
  watchlists: PricePromoWatchlist[];
  loading: boolean;
  error: string | null;
  /** Max active watchlists per agent. */
  limit: number;
  /** Watchlist ids that currently have unseen matches — rendered blinking. */
  newMatchWatchlistIds: Set<number>;
  onCreate: (input: PricePromoWatchlistInput) => Promise<void>;
  onUpdate: (id: number, input: PricePromoWatchlistInput) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  /** Fired when the agent opens a watchlist's matches; clears its "new" state. */
  onMarkSeen: (id: number) => void;
  formatSqft: (size: number | string | null | undefined) => string;
  onUnitClick: (unit: PricePromoUnit) => void;
  onPriceClick: (unit: PricePromoUnit) => void;
}

/**
 * Price Promo Watchlist: per-client alerts on the price promo feed. The list
 * view shows every watchlist (client, criteria, unseen-match badge); tapping
 * one opens its matches split into "Latest Price Promo", "Top 5 Saving" and
 * "All Price Promo" sections mirroring the Price Promo Units dialog.
 */
const PricePromoWatchlistDialog: React.FC<PricePromoWatchlistDialogProps> = ({
  open,
  onClose,
  tab,
  onTabChange,
  anticipate,
  anticipateNewCount,
  units,
  watchlists,
  loading,
  error,
  limit,
  newMatchWatchlistIds,
  onCreate,
  onUpdate,
  onDelete,
  onMarkSeen,
  formatSqft,
  onUnitClick,
  onPriceClick,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [latestExpanded, setLatestExpanded] = useState<boolean>(true);
  const [topSavingExpanded, setTopSavingExpanded] = useState<boolean>(true);
  // "All Price Promo" starts collapsed — it is the longest section, same as the Price Promo Units dialog.
  const [allExpanded, setAllExpanded] = useState<boolean>(false);

  // Create / edit form dialog
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [formTarget, setFormTarget] = useState<PricePromoWatchlist | null>(null);
  const [form, setForm] = useState<WatchlistFormState>(EMPTY_FORM);
  const [formSaving, setFormSaving] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteArmed, setDeleteArmed] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setFormOpen(false);
    }
  }, [open]);

  // Built-in Demo Client row pinned above the agent's watchlists; tracks the
  // latest promo unit and stays outside the quota (it is never persisted).
  const demoWatchlist = useMemo(() => buildDemoWatchlist(units), [units]);

  const selected = useMemo(() => {
    if (selectedId === null) return null;
    if (demoWatchlist && selectedId === demoWatchlist.id) return demoWatchlist;
    return watchlists.find((w) => w.id === selectedId) ?? null;
  }, [watchlists, selectedId, demoWatchlist]);

  const matchCounts = useMemo(() => {
    const map = new Map<number, number>();
    for (const w of demoWatchlist ? [demoWatchlist, ...watchlists] : watchlists) {
      map.set(w.id, getWatchlistMatches(units, w).matches.length);
    }
    return map;
  }, [units, watchlists, demoWatchlist]);

  const selectedMatches = useMemo(
    () => (selected ? getWatchlistMatches(units, selected) : null),
    [units, selected],
  );

  // The list view's match count is the post-creation set, so spell out both
  // numbers here whenever the all-time total is larger — otherwise "97 units"
  // reads as a contradiction of the "56 matches" on the watchlist card.
  const allSectionSubtitle = useMemo(() => {
    const allCount = selectedMatches?.all.length ?? 0;
    const sinceCount = selectedMatches?.matches.length ?? 0;
    const allLabel = `${allCount} unit${allCount === 1 ? '' : 's'}`;
    return allCount > sinceCount ? `${allLabel} all-time · ${sinceCount} since created` : allLabel;
  }, [selectedMatches]);

  const atLimit = watchlists.length >= limit;

  /** The open watchlist detail — only meaningful on the Watchlist tab. */
  const watchlistDetail = tab === 'watchlist' ? selected : null;

  const handleTabChange = (next: PricePromoWatchlistDialogTab) => {
    if (next === tab) return;
    setSelectedId(null);
    onTabChange(next);
  };

  const openWatchlist = (watchlist: PricePromoWatchlist) => {
    setSelectedId(watchlist.id);
    setLatestExpanded(true);
    setTopSavingExpanded(true);
    setAllExpanded(false);
    if (watchlist.id !== DEMO_WATCHLIST_ID) onMarkSeen(watchlist.id);
  };

  const openCreateForm = () => {
    setFormTarget(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDeleteArmed(false);
    setFormOpen(true);
  };

  const openEditForm = (watchlist: PricePromoWatchlist) => {
    setFormTarget(watchlist);
    setForm({
      clientName: watchlist.clientName,
      mobile: watchlist.mobile,
      bedrooms: [...watchlist.bedrooms],
      budget: formatBudgetInput(String(Math.round(watchlist.budget))),
      regions: [...watchlist.regions],
      districts: [...watchlist.districts],
    });
    setFormError(null);
    setDeleteArmed(false);
    setFormOpen(true);
  };

  const handleFormSave = async () => {
    const clientName = form.clientName.trim();
    const mobile = form.mobile.trim();
    const budget = Number(form.budget.replace(/[,$\s]/g, ''));
    if (!clientName) { setFormError('Please enter the client name'); return; }
    if (!mobile) { setFormError('Please enter the mobile number'); return; }
    if (form.bedrooms.length === 0) { setFormError('Please select at least one bedroom count'); return; }
    if (!Number.isFinite(budget) || budget <= 0) { setFormError('Please enter a valid budget'); return; }

    const input: PricePromoWatchlistInput = {
      clientName,
      mobile,
      bedrooms: [...form.bedrooms].sort((a, b) => a - b),
      budget,
      regions: form.regions,
      districts: [...form.districts].sort((a, b) => Number(a.slice(1)) - Number(b.slice(1))),
    };
    try {
      setFormSaving(true);
      setFormError(null);
      if (formTarget) {
        await onUpdate(formTarget.id, input);
      } else {
        await onCreate(input);
      }
      setFormOpen(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save watchlist');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!formTarget) return;
    if (!deleteArmed) {
      setDeleteArmed(true);
      return;
    }
    try {
      setFormSaving(true);
      setFormError(null);
      await onDelete(formTarget.id);
      setFormOpen(false);
      if (selectedId === formTarget.id) setSelectedId(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to delete watchlist');
    } finally {
      setFormSaving(false);
    }
  };

  const renderCriteriaChips = (watchlist: PricePromoWatchlist) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      <Chip
        label={formatBedrooms(watchlist.bedrooms)}
        size="small"
        sx={{ bgcolor: 'rgba(var(--color-accent-rgb), 0.12)', color: 'var(--color-accent)', fontWeight: 700 }}
      />
      <Chip
        label={`${formatBudget(watchlist.budget)} ±10%`}
        size="small"
        sx={{ bgcolor: 'rgba(var(--color-success-rgb, 76, 175, 80), 0.12)', color: 'var(--color-success)', fontWeight: 700 }}
      />
      {watchlist.regions.length > 0 && (
        <Chip
          label={watchlist.regions.join(' · ')}
          size="small"
          sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.1)', color: 'var(--color-text-strong)', fontWeight: 700 }}
        />
      )}
      {watchlist.districts.length > 0 && (
        <Chip
          label={formatDistricts(watchlist.districts)}
          size="small"
          sx={{ bgcolor: 'rgba(var(--color-info-navy-rgb, 40, 70, 120), 0.25)', color: 'var(--color-text-strong)', fontWeight: 700 }}
        />
      )}
    </Box>
  );

  const renderWatchlistCard = (watchlist: PricePromoWatchlist, isDemo: boolean) => {
    const hasNew = !isDemo && newMatchWatchlistIds.has(watchlist.id);
    const matchCount = matchCounts.get(watchlist.id) ?? 0;
    return (
      <Card
        key={watchlist.id}
        variant="outlined"
        onClick={() => openWatchlist(watchlist)}
        sx={{
          borderRadius: 2,
          border: hasNew ? '2px solid #f44336' : '1px solid rgba(var(--color-text-rgb), 0.12)',
          backgroundColor: 'rgba(var(--color-shadow-rgb), 0.25)',
          boxShadow: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: hasNew ? '#f44336' : 'var(--color-info-navy)',
            backgroundColor: 'rgba(var(--color-sheet-rgb), 0.35)',
          },
          ...(hasNew
            ? {
                animation: 'watchlist-new-pulse 1.2s ease-in-out infinite',
                '@keyframes watchlist-new-pulse': {
                  '0%, 100%': { backgroundColor: 'rgba(244, 67, 54, 0.06)' },
                  '50%': { backgroundColor: 'rgba(244, 67, 54, 0.22)' },
                },
              }
            : {}),
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          {/* Client identity */}
          <Box sx={{ minWidth: 0, width: { xs: '100%', sm: 168 }, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <PersonIcon sx={{ fontSize: 18, color: 'var(--color-accent)', flexShrink: 0 }} />
              <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ color: 'var(--color-text-strong)', minWidth: 0 }}>
                {watchlist.clientName}
              </Typography>
              {isDemo && (
                <Chip
                  label="DEMO"
                  size="small"
                  sx={{ height: 18, fontSize: 10, fontWeight: 800, bgcolor: 'var(--color-info-navy)', color: '#fff', flexShrink: 0 }}
                />
              )}
              {hasNew && (
                <Chip
                  icon={<FiberNewIcon sx={{ fontSize: 16 }} />}
                  label="NEW"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 800,
                    bgcolor: '#f44336',
                    color: '#fff',
                    flexShrink: 0,
                    animation: 'watchlist-badge-pulse 1.2s ease-in-out infinite',
                    '@keyframes watchlist-badge-pulse': {
                      '0%, 100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)' },
                      '50%': { boxShadow: '0 0 0 6px rgba(244, 67, 54, 0)' },
                    },
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <PhoneIcon sx={{ fontSize: 13, color: 'rgba(var(--color-text-rgb), 0.55)' }} />
              <Typography variant="caption" noWrap sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                {watchlist.mobile}
              </Typography>
            </Box>
          </Box>

          {/* Criteria fills the middle */}
          <Box sx={{ flex: 1, minWidth: 0 }}>{renderCriteriaChips(watchlist)}</Box>

          {/* Match count stat — post-creation promos only, spelled out so it reads
              consistently with the detail view's all-time "All Price Promo" count. */}
          <Box sx={{ flexShrink: 0, textAlign: 'center', minWidth: 96 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.15rem',
                lineHeight: 1.1,
                color: matchCount > 0 ? 'var(--color-success)' : 'rgba(var(--color-text-rgb), 0.35)',
              }}
            >
              {matchCount}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', lineHeight: 1.1, display: 'block' }}>
              matched
            </Typography>
            <Typography
              sx={{
                fontSize: '0.6rem',
                lineHeight: 1.15,
                display: 'block',
                mt: 0.25,
                color: 'rgba(var(--color-text-rgb), 0.4)',
              }}
            >
              (since watchlist created)
            </Typography>
          </Box>

          {!isDemo ? (
            <Tooltip title="Edit watchlist">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditForm(watchlist);
                }}
                sx={{ flexShrink: 0, color: 'rgba(var(--color-text-rgb), 0.6)', '&:hover': { color: 'var(--color-accent)' } }}
                aria-label={`Edit watchlist for ${watchlist.clientName}`}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            // Invisible stand-in for the edit button so the match count
            // column lines up with the editable rows below.
            <IconButton size="small" disabled aria-hidden sx={{ flexShrink: 0, visibility: 'hidden' }}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Card>
    );
  };

  const renderUnitCard = (unit: PricePromoUnit, keyPrefix: string) => (
    <Card
      key={`${keyPrefix}-${unit.ProjectId}-${unit.id}`}
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(var(--color-text-rgb), 0.12)',
        backgroundColor: 'rgba(var(--color-shadow-rgb), 0.25)',
        boxShadow: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'var(--color-info-navy)',
          backgroundColor: 'rgba(var(--color-sheet-rgb), 0.35)',
        },
      }}
      onClick={() => onUnitClick(unit)}
    >
      <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'var(--color-text-strong)' }}>
              {unit.projectName}
            </Typography>
            {unit.isLive === 1 && (
              <Chip
                label="LIVE"
                size="small"
                sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(244, 67, 54, 0.15)', color: '#f44336', flexShrink: 0 }}
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.85)' }}>
            Unit {unit.UnitNumber} &middot; {unit.bedroomNumber}BR{isPenthouseUnit(unit) ? ' (PH)' : ''} &middot; {formatSqft(unit.size)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--color-success)', fontWeight: 600, display: 'block' }}>
            {unit.priceChange < 0 ? '-' : ''}${Math.abs(unit.priceChange).toLocaleString()}
            {unit.priceChangePercent ? ` (${unit.priceChangePercent.toFixed(1)}%)` : ''}
          </Typography>
          {unit.priceChangeAt && (
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', display: 'block' }}>
              {formatDate(unit.priceChangeAt)}
            </Typography>
          )}
        </Box>
        <PricePromoPriceButton
          priceLabel={`$${Math.round(unit.price).toLocaleString()}`}
          onClick={() => onPriceClick(unit)}
        />
      </Box>
    </Card>
  );

  /**
   * Collapsible section of unit cards. `color` drives the accented look of the
   * highlight sections; `options.neutral` renders the plain grey treatment the
   * Price Promo Units dialog gives its "All Price Promo" section.
   */
  const renderSection = (
    title: string,
    subtitle: string,
    color: string,
    expanded: boolean,
    onToggle: () => void,
    sectionUnits: PricePromoUnit[],
    emptyText: string,
    keyPrefix: string,
    options?: { neutral?: boolean },
  ) => {
    const neutral = options?.neutral === true;
    const border = neutral ? '1px solid rgba(var(--color-text-rgb), 0.15)' : `2px solid ${color}`;
    const headerBg = neutral ? 'rgba(var(--color-shadow-rgb), 0.2)' : `${color}14`;
    const headerHoverBg = neutral ? 'rgba(var(--color-sheet-rgb), 0.25)' : `${color}24`;
    const titleColor = neutral ? 'var(--color-text-strong)' : color;
    const bodyBg = neutral ? 'transparent' : `${color}0a`;
    return (
      <Box sx={{ borderRadius: 2.5, border, overflow: 'hidden' }}>
        <Box
          onClick={onToggle}
          sx={{
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            cursor: 'pointer',
            bgcolor: headerBg,
            '&:hover': { bgcolor: headerHoverBg },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: titleColor }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block' }}>
              {subtitle}
            </Typography>
          </Box>
          <ExpandMoreIcon sx={{ color: titleColor, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 1.25, py: 1.25, bgcolor: bodyBg }}>
            {sectionUnits.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                {emptyText}
              </Typography>
            ) : (
              <Stack spacing={1}>{sectionUnits.map((u) => renderUnitCard(u, keyPrefix))}</Stack>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'var(--color-surface)',
            color: 'rgba(var(--color-text-rgb), 0.9)',
            boxShadow: '0 4px 24px rgba(var(--color-shadow-rgb), 0.4)',
            border: '1px solid rgba(var(--color-text-rgb), 0.08)',
          },
        }}
      >
        <DialogTitle sx={{ pb: watchlistDetail ? 1.5 : 0, color: 'var(--color-text-strong)', fontWeight: 600, borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              {watchlistDetail ? (
                <IconButton size="small" onClick={() => setSelectedId(null)} sx={{ color: 'var(--color-text-strong)' }} aria-label="Back to watchlists">
                  <ArrowBackIcon />
                </IconButton>
              ) : tab === 'watchlist' ? (
                <BinocularsIcon sx={{ color: 'var(--color-accent)' }} />
              ) : (
                <UpcomingIcon sx={{ color: 'var(--color-accent)' }} />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: 'var(--color-text-strong)' }}>
                  {watchlistDetail
                    ? watchlistDetail.clientName
                    : tab === 'watchlist'
                      ? 'Price Promo Watchlist'
                      : 'Anticipate Price Promo'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                  {watchlistDetail
                    ? `${watchlistDetail.mobile} · ${formatBedrooms(watchlistDetail.bedrooms)} · ${formatBudget(watchlistDetail.budget)}`
                      + `${watchlistDetail.regions.length ? ` · ${watchlistDetail.regions.join(' · ')}` : ''}`
                      + `${watchlistDetail.districts.length ? ` · ${formatDistricts(watchlistDetail.districts)}` : ''}`
                    : tab === 'watchlist'
                      ? `${watchlists.length} of ${limit} watchlists`
                      : `${anticipate.items.length} upcoming promo${anticipate.items.length === 1 ? '' : 's'} from project teams`}
                </Typography>
              </Box>
            </Box>
            {watchlistDetail && watchlistDetail.id !== DEMO_WATCHLIST_ID && (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => openEditForm(watchlistDetail)}
                sx={{ color: 'var(--color-accent)', textTransform: 'none', fontWeight: 700 }}
              >
                Edit
              </Button>
            )}
          </Box>

          {!watchlistDetail && (
            <Tabs
              value={tab}
              onChange={(_, next) => handleTabChange(next as PricePromoWatchlistDialogTab)}
              variant="fullWidth"
              sx={{
                mt: 1,
                minHeight: 42,
                '& .MuiTabs-indicator': { backgroundColor: 'var(--color-accent)', height: 3, borderRadius: 1.5 },
              }}
            >
              <Tab
                value="watchlist"
                icon={<BinocularsIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Watchlist"
                sx={{
                  minHeight: 42,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'rgba(var(--color-text-rgb), 0.6)',
                  '&.Mui-selected': { color: 'var(--color-accent)' },
                }}
              />
              <Tab
                value="anticipate"
                icon={<UpcomingIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={
                  <Badge
                    badgeContent={anticipateNewCount}
                    color="error"
                    max={9}
                    sx={{ '& .MuiBadge-badge': { right: -14, top: 2, fontWeight: 800 } }}
                  >
                    Anticipate
                  </Badge>
                }
                sx={{
                  minHeight: 42,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'rgba(var(--color-text-rgb), 0.6)',
                  '&.Mui-selected': { color: 'var(--color-accent)' },
                  pr: anticipateNewCount > 0 ? 3 : 2,
                }}
              />
            </Tabs>
          )}
        </DialogTitle>

        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, bgcolor: 'var(--color-surface)', borderColor: 'rgba(var(--color-text-rgb), 0.08)', color: 'rgba(var(--color-text-rgb), 0.9)' }}>
          {tab === 'anticipate' ? (
            <AnticipatePricePromoTab {...anticipate} />
          ) : (
            <>
              {loading && <LinearProgress sx={{ mb: 2 }} />}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {!selected ? (
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreateForm}
                    disabled={atLimit}
                    sx={{
                      bgcolor: 'var(--color-accent)',
                      color: '#fff',
                      fontWeight: 700,
                      textTransform: 'none',
                      borderRadius: 2,
                      py: 1,
                      '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(1.08)' },
                      '&.Mui-disabled': { bgcolor: 'rgba(var(--color-text-rgb), 0.12)', color: 'rgba(var(--color-text-rgb), 0.4)' },
                    }}
                  >
                    {atLimit ? `Maximum ${limit} watchlists reached` : 'Create Watchlist'}
                  </Button>

                  {demoWatchlist && renderWatchlistCard(demoWatchlist, true)}

                  {watchlists.length === 0 && !loading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <BinocularsIcon sx={{ fontSize: 40, color: 'rgba(var(--color-text-rgb), 0.25)', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                        No watchlists yet. Create one to get alerted when a price promo matches your client's budget and bedroom.
                      </Typography>
                    </Box>
                  ) : (
                    watchlists.map((watchlist) => renderWatchlistCard(watchlist, false))
                  )}

                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', px: 0.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: 'rgba(var(--color-text-rgb), 0.5)', mt: '1px' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
                      {WATCHLIST_MATCH_EXPLANATION}
                      {demoWatchlist ? ` ${DEMO_WATCHLIST_EXPLANATION}` : ''}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Stack spacing={2} sx={{ mt: 0.5 }}>
                  {selectedMatches?.usedFallback && (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      No new promos since this watchlist was created — showing the latest promo that fits the criteria.
                    </Alert>
                  )}

                  {renderSection(
                    `Latest Price Promo (last ${WATCHLIST_LATEST_DAYS} days)`,
                    `${selectedMatches?.latest.length ?? 0} unit${(selectedMatches?.latest.length ?? 0) === 1 ? '' : 's'}`,
                    '#f44336',
                    latestExpanded,
                    () => setLatestExpanded((v) => !v),
                    selectedMatches?.latest ?? [],
                    `No matching price promo in the last ${WATCHLIST_LATEST_DAYS} days.`,
                    'wl-latest',
                  )}

                  {renderSection(
                    'Top 5 Saving',
                    `${selectedMatches?.topSaving.length ?? 0} unit${(selectedMatches?.topSaving.length ?? 0) === 1 ? '' : 's'}`,
                    '#ed6c02',
                    topSavingExpanded,
                    () => setTopSavingExpanded((v) => !v),
                    selectedMatches?.topSaving ?? [],
                    'No matching price promo units yet.',
                    'wl-saving',
                  )}

                  {renderSection(
                    'All Price Promo',
                    allSectionSubtitle,
                    'var(--color-text-strong)',
                    allExpanded,
                    () => setAllExpanded((v) => !v),
                    selectedMatches?.all ?? [],
                    "No price promo units fit this client's criteria yet.",
                    'wl-all',
                    { neutral: true },
                  )}

                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', px: 0.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: 'rgba(var(--color-text-rgb), 0.5)', mt: '1px' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
                      {WATCHLIST_MATCH_EXPLANATION} {WATCHLIST_ALL_SECTION_EXPLANATION}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)', bgcolor: 'var(--color-surface)' }}>
          <Button onClick={onClose} sx={{ color: 'rgba(var(--color-text-rgb), 0.9)' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create / edit form */}
      <Dialog
        open={formOpen}
        onClose={() => { if (!formSaving) setFormOpen(false); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'var(--color-surface)',
            color: 'rgba(var(--color-text-rgb), 0.9)',
            boxShadow: '0 4px 24px rgba(var(--color-shadow-rgb), 0.4)',
            border: '1px solid rgba(var(--color-text-rgb), 0.08)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'var(--color-text-strong)', fontWeight: 600, borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          {formTarget ? 'Edit Watchlist' : 'Create Watchlist'}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'var(--color-surface)', borderColor: 'rgba(var(--color-text-rgb), 0.08)' }}>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Client Name"
              value={form.clientName}
              onChange={(e) => setForm((f) => ({ ...f, clientName: e.target.value }))}
              size="small"
              fullWidth
              required
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Mobile"
              value={form.mobile}
              onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
              size="small"
              fullWidth
              required
              type="tel"
              inputProps={{ maxLength: 20 }}
            />
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', fontWeight: 600, display: 'block', mb: 0.75 }}>
                Bedroom * (select one or more)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {BEDROOM_OPTIONS.map((bed) => {
                  const active = form.bedrooms.includes(bed);
                  return (
                    <Chip
                      key={bed}
                      label={bedroomLabel(bed)}
                      size="small"
                      clickable
                      onClick={() => setForm((f) => ({ ...f, bedrooms: toggleValue(f.bedrooms, bed) }))}
                      variant={active ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 600,
                        bgcolor: active ? 'var(--color-info-navy)' : 'transparent',
                        color: active ? '#fff' : 'rgba(var(--color-text-rgb), 0.85)',
                        borderColor: 'rgba(var(--color-text-rgb), 0.25)',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
            <TextField
              label="Budget (SGD)"
              value={form.budget}
              onChange={(e) => setForm((f) => ({ ...f, budget: formatBudgetInput(e.target.value) }))}
              size="small"
              fullWidth
              required
              inputProps={{ inputMode: 'numeric', maxLength: 15 }}
              helperText="Promos priced within ±10% of this budget will match"
            />
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', fontWeight: 600, display: 'block', mb: 0.75 }}>
                Region (optional, select one or more — empty matches any)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {REGION_OPTIONS.map((region) => {
                  const active = form.regions.includes(region);
                  return (
                    <Chip
                      key={region}
                      label={region}
                      size="small"
                      clickable
                      onClick={() => setForm((f) => ({ ...f, regions: toggleValue(f.regions, region) }))}
                      variant={active ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 600,
                        bgcolor: active ? 'var(--color-info-navy)' : 'transparent',
                        color: active ? '#fff' : 'rgba(var(--color-text-rgb), 0.85)',
                        borderColor: 'rgba(var(--color-text-rgb), 0.25)',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', fontWeight: 600, display: 'block', mb: 0.75 }}>
                District (optional, select one or more — empty matches any)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {DISTRICT_OPTIONS.map((district) => {
                  const active = form.districts.includes(district);
                  return (
                    <Chip
                      key={district}
                      label={district}
                      size="small"
                      clickable
                      onClick={() => setForm((f) => ({ ...f, districts: toggleValue(f.districts, district) }))}
                      variant={active ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 600,
                        minWidth: 44,
                        bgcolor: active ? 'var(--color-info-navy)' : 'transparent',
                        color: active ? '#fff' : 'rgba(var(--color-text-rgb), 0.85)',
                        borderColor: 'rgba(var(--color-text-rgb), 0.25)',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)', bgcolor: 'var(--color-surface)', px: 2, py: 1.5, gap: 1 }}>
          {formTarget && (
            <Button
              onClick={handleDelete}
              disabled={formSaving}
              startIcon={<DeleteIcon />}
              sx={{ color: '#f44336', textTransform: 'none', fontWeight: 700, mr: 'auto' }}
            >
              {deleteArmed ? 'Confirm delete?' : 'Delete'}
            </Button>
          )}
          <Button onClick={() => setFormOpen(false)} disabled={formSaving} sx={{ color: 'rgba(var(--color-text-rgb), 0.9)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleFormSave}
            disabled={formSaving}
            variant="contained"
            sx={{
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(1.08)' },
            }}
          >
            {formSaving ? 'Saving…' : formTarget ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PricePromoWatchlistDialog;
